"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import type { Event } from "@/types";

type EventsDesktopViewProps = {
  events: Event[];
  pagination: {
    currentPage: number;
    lastPage: number;
    total: number;
  };
};

function EventCard({ event }: { event: Event }) {
  const [imageLoading, setImageLoading] = useState(true);

  return (
    <Link
      href={event.slug ? `/evento/${event.slug}` : "#"}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
    >
      {/* Image */}
      <div className="relative aspect-16/10 overflow-hidden bg-gray-100">
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}
        <Image
          src={event.image}
          alt={event.title}
          fill
          className={`object-cover transition-transform duration-500 group-hover:scale-105 ${
            imageLoading ? "opacity-0" : "opacity-100"
          }`}
          onLoad={() => setImageLoading(false)}
          unoptimized
        />

        {/* Date Badge */}
        <div className="absolute top-4 left-4 px-3 py-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 leading-none">
              {event.date.split(" ")[0]}
            </div>
            <div className="text-xs text-mygreen font-semibold uppercase tracking-wider">
              {event.date.split(" ")[1]}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-bold text-gray-900 text-lg mb-3 line-clamp-2 group-hover:text-mygreen transition-colors leading-tight">
          {event.title}
        </h3>

        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-4 h-4 text-mygreen shrink-0" />
            <span>{event.hour}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-4 h-4 text-mygreen shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function EventsDesktopView({
  events,
  pagination,
}: EventsDesktopViewProps) {
  if (events.length === 0) {
    return (
      <div className="hidden md:block py-16">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.3em] text-mygreen font-semibold mb-3">
              Agenda Santa Fé
            </p>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Eventos da Cidade
            </h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              Do show na praça aos encontros de empreendedorismo, descubra tudo
              o que vai acontecer em Santa Fé do Sul.
            </p>
          </div>

          {/* Empty State */}
          <div className="max-w-md mx-auto bg-white rounded-2xl p-10 shadow-sm text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-gray-100 flex items-center justify-center">
              <CalendarIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Nenhum evento encontrado
            </h2>
            <p className="text-gray-500 mb-6">
              Ainda não temos eventos cadastrados. Volte em breve!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/"
                className="px-6 py-3 bg-mygreen text-white rounded-full font-semibold hover:bg-mygreen/90 transition-colors"
              >
                Voltar para Home
              </Link>
              <Link
                href="/contato"
                className="px-6 py-3 border-2 border-mygreen text-mygreen rounded-full font-semibold hover:bg-mygreen/10 transition-colors"
              >
                Divulgar Evento
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden md:block py-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.3em] text-mygreen font-semibold mb-3">
            Agenda Santa Fé
          </p>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Eventos da Cidade
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Do show na praça aos encontros de empreendedorismo, descubra tudo o
            que vai acontecer em Santa Fé do Sul.
          </p>
        </div>

        {/* Stats */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-6 px-6 py-3 bg-mygreen/5 rounded-full">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-mygreen" />
              <span className="text-gray-900 font-medium">
                {pagination.total}{" "}
                {pagination.total === 1 ? "evento" : "eventos"}
              </span>
            </div>
            {pagination.lastPage > 1 && (
              <>
                <div className="w-px h-5 bg-gray-300" />
                <span className="text-gray-600">
                  Página {pagination.currentPage} de {pagination.lastPage}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Events Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>

        {/* Pagination */}
        {pagination.lastPage > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
          />
        )}
      </div>
    </div>
  );
}

function Pagination({
  currentPage,
  lastPage,
}: {
  currentPage: number;
  lastPage: number;
}) {
  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= lastPage;

  // Generate page numbers
  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];

    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(lastPage - 1, currentPage + 1);
        i++
      ) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < lastPage - 2) {
        pages.push("...");
      }

      if (!pages.includes(lastPage)) {
        pages.push(lastPage);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className="flex items-center justify-center gap-2"
      aria-label="Paginação"
    >
      {/* First Page */}
      {currentPage > 2 && (
        <Link
          href="/eventos"
          className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Primeira página"
        >
          <ChevronDoubleLeftIcon className="w-5 h-5" />
        </Link>
      )}

      {/* Previous */}
      {prevDisabled ? (
        <span className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-300 cursor-not-allowed">
          <ChevronLeftIcon className="w-5 h-5" />
        </span>
      ) : (
        <Link
          href={
            currentPage === 2 ? "/eventos" : `/eventos?page=${currentPage - 1}`
          }
          className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Página anterior"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </Link>
      )}

      {/* Page Numbers */}
      <div className="flex items-center gap-1 mx-2">
        {pageNumbers.map((page, index) =>
          page === "..." ? (
            <span
              key={`ellipsis-${index}`}
              className="flex items-center justify-center w-10 h-10 text-gray-400"
            >
              ···
            </span>
          ) : (
            <Link
              key={page}
              href={page === 1 ? "/eventos" : `/eventos?page=${page}`}
              className={`flex items-center justify-center w-10 h-10 rounded-lg font-medium transition-all ${
                page === currentPage
                  ? "bg-mygreen text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </Link>
          )
        )}
      </div>

      {/* Next */}
      {nextDisabled ? (
        <span className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-300 cursor-not-allowed">
          <ChevronRightIcon className="w-5 h-5" />
        </span>
      ) : (
        <Link
          href={`/eventos?page=${currentPage + 1}`}
          className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Próxima página"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </Link>
      )}

      {/* Last Page */}
      {currentPage < lastPage - 1 && (
        <Link
          href={`/eventos?page=${lastPage}`}
          className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Última página"
        >
          <ChevronDoubleRightIcon className="w-5 h-5" />
        </Link>
      )}
    </nav>
  );
}
